import { mapGetters } from 'vuex'
import { validateFileExt } from '@/utils/validate'
import { xlsxUtils, xlsxWriteFile, xlsxRead } from '@/utils/excel'
import { omit } from 'lodash-es'

import {
  TASK_TYPE_ENUM,
  LEVEL_ONE_APPEAL,
  levelOneAppealMap,
  fileExtType,
  rightFileExtType
} from '../../const'

// 投诉歌单上传
const complaintCol = {
  '原曲-歌曲ID': '',
  竞品ID: '',
  '原曲-歌曲名': '',
  '原曲-歌手': '',
  '原曲-专辑': '',
  '原曲-版本': '',
  '原曲-词作者': '',
  '原曲-曲作者': '',
  竞品权利类型: '',
  竞品权利人: '',
  '侵权-平台': '',
  '侵权-链接': '',
  '侵权-歌曲名/视频名': '',
  '侵权-歌手名': '',
  '侵权-歌曲专辑名': '',
  '侵权-歌曲版本': '',
  竞品首次投诉日期: '',
  竞品最新投诉日期: '',
  竞品投诉备注: '',
  '侵权-媒体类型': '',
  状态: '',
  权利文件: '',
  关联申诉单号: '',
  原曲授权方ID: '',
  原曲原始版权方ID: '',
  版权开始时间: '',
  版权结束时间: '',
  词比例: '',
  曲比例: '',
  是否同步至高危曲库: ''
}

// 白名单歌单上传
const whitelistCol = {
  关联申诉单号: '',
  '白名单歌曲信息-TME ID': '',
  '白名单歌曲信息-歌曲名': '',
  '白名单歌曲信息-歌手名': '',
  '白名单歌曲信息-版本': '',
  '白名单歌曲信息-专辑名': '',
  '原作品-歌曲名': '',
  '原作品-词作者': '',
  '原作品-曲作者': '',
  原曲歌手: '',
  '原曲-TME ID': '',
  状态: '',
  权利文件: ''
}

// 下架歌单上传
const offlineCol = {
  关联申诉单号: '',
  'TME ID': '',
  媒体类型: '',
  '歌曲/视频名': '',
  歌手: '',
  版本: '',
  专辑: '',
  词作者: '',
  曲作者: '',
  平台: '',
  链接: '',
  '歌曲/视频别名': '',
  歌手别名: '',
  曲序: '',
  专辑别名: '',
  歌曲编码: '',
  isrc: '',
  专辑编码: '',
  upc: '',
  发行时间: '',
  状态: '',
  权利文件: '',
}

// 匹配上传
const matchCol = {
  投诉歌单ID: '',
  'TME ID': '',
  'TME ID 类型': '',
  确权状态: '',
  期限: '',
  匹配时平台ID: '',
  匹配时平台状态: '',
  匹配时版权状态: '',
  '匹配时版权公司(填ID)': '',
  '匹配时原始版权公司(填ID)': '',
  备注: '',
  经办人: '',
}

// 版权认领上传
const noticeCol = {
  '歌曲/视频名': '',
  歌手: '',
  专辑: '',
  平台: '',
  链接: '',
  申诉Id: '',
}

const colMap = {
  [LEVEL_ONE_APPEAL.TORT.code]: complaintCol,
  [LEVEL_ONE_APPEAL.WHITE_LIST.code]: whitelistCol,
  [LEVEL_ONE_APPEAL.OFFLINE.code]: offlineCol,
  [LEVEL_ONE_APPEAL.CP_NOTICE.code]: noticeCol,
}

// 校验文件内容
function validateFileContent(file, columns, mode, bussType) {
  return new Promise((resolve) => {
    if (mode !== TASK_TYPE_ENUM.MATCH) {
      resolve([true])
    }

    // 使用xlsx读取文件内容，检查【TME ID 类型】列是否有空值
    const checkKeys = ['TME ID 类型']
    const reader = new FileReader()
    reader.onload = (e) => {
      const data = new Uint8Array(e.target.result)
      const workbook = xlsxRead(data, { type: 'array' })
      const dataList = xlsxUtils.sheet_to_json(workbook.Sheets[workbook.SheetNames[0]], { raw: false })

      for (let index = 0; index < dataList.length; index++) {
        const row = dataList[index]
        for (const key of checkKeys) {
          if (!row[key]) {
            resolve([false, `第 ${index + 2} 行【${key}】列存在空值，请检查`])
          }
        }
      }

      resolve([true])
    }

    reader.onerror = () => {
      resolve([false, '文件读取失败'])
    }

    reader.readAsArrayBuffer(file)
  })
}

export default {
  props: {
    visible: Boolean,
    mode: {
      type: Number,
      default: TASK_TYPE_ENUM.COMPLAINT
    },
    bussType: {
      type: Number,
      default: LEVEL_ONE_APPEAL.TORT.code
    },
    loading: {
      type: Boolean,
      default: false
    },
  },
  data() {
    return {
      TASK_TYPE_ENUM,
      LEVEL_ONE_APPEAL,
      levelOneAppealMap,
      fileExtType,
      rightFileExtType,

      fileList: [],
      rightFileList: [],
    }
  },
  computed: {
    ...mapGetters([
      'isExternalUser',
      'isEditor',
      'isAdmin'
    ]),
    canCheck() {
      return this.isEditor || this.isAdmin
    },
    showRightFileUpload() {
      return this.mode !== TASK_TYPE_ENUM.MATCH
    },
    columns() {
      if (this.mode === TASK_TYPE_ENUM.MATCH) {
        if (this.isEdit) {
          return [{
            匹配结果ID: '',
            ...matchCol
          }]
        }
        return [matchCol]
      }

      const taskIdKeys = ['关联申诉单号']
      const tmeIdKeys = ['原曲-歌曲ID', 'TME ID']
      const statusKeys = ['状态']
      const cols = colMap[this.bussType]
      const omitKeys = []
      if (this.isExternalUser) {
        omitKeys.push(...tmeIdKeys)
      }
      if (!this.canCheck) {
        omitKeys.push(...statusKeys)
      }
      if (this.mode === TASK_TYPE_ENUM.COMPLAINT) {
        omitKeys.push(taskIdKeys)
      }
      if (this.isEdit) { return [{ 歌单ID: '', ...omit(cols, omitKeys) }] }
      return [omit(cols, omitKeys)]
    },
  },
  methods: {
    genTemplate() {
      const workbook = { SheetNames: ['Sheet1'], Sheets: {}, Props: {} }
      workbook.Sheets.Sheet1 = xlsxUtils.json_to_sheet(this.columns)
      xlsxWriteFile(workbook, 'batch_template.xlsx')
    },

    handleFileRemove(file, fileList) {
      this.fileList = fileList
    },
    handleFileError(err, file, fileList) {
      this.fileList = fileList
      this.$message.error('文件上传失败: ' + err.message)
    },
    handleFileSuccess(resp, file, fileList) {
      this.handleSuccess(resp, file)
      this.fileList = fileList.slice(-1)
    },
    async handleFileBeforeUpload(file) {
      const res = this.handleBeforeUpload(fileExtType, file, this.fileList)
      // 判断Promise是否为reject
      if (!res) return Promise.reject()

      // 校验文件内容
      const [isValid, msg] = await validateFileContent(file, this.columns, this.mode, this.bussType)
      if (!isValid) {
        this.$message.error(msg)
        return Promise.reject()
      }

      return Promise.resolve()
    },

    handleRightFileRemove(file, fileList) {
      this.rightFileList = fileList
    },
    handleRightFileError(err, file, fileList) {
      this.rightFileList = fileList
      this.$message.error('授权文件上传失败: ' + err.message)
    },
    handleRightFileSuccess(resp, file, fileList) {
      this.handleSuccess(resp, file)
      this.rightFileList = fileList.slice(-1)
    },

    handleRightFileBeforeUpload(file) {
      const res = this.handleBeforeUpload(rightFileExtType, file, this.rightFileList)
      return res ? Promise.resolve() : Promise.reject()
    },
    handlePreview(file) {
      open(file.url, '_blank')
    },
    handleSuccess(resp, file) {
      file.fileUrl = file.url = resp.data.url
      file.filePath = file.path = resp.data.file_id
      file.fileName = file.name
      file.md5 = resp.data.md5
    },
    handleBeforeUpload(extType, file, fileList) {
      if (!validateFileExt(extType, file.name)) {
        this.$message.warning('不支持的文件类型')
        return false
      }

      const sameFile = fileList.find(v => v.md5 === file.md5)
      if (sameFile) {
        this.$message.warning(`文件已存在，请检查【${file.name}】和【${sameFile.name}】`)
        return false
      }

      return true
    },
  }
}
